// src/pages/AdminDashboard.tsx
//
// Standalone admin page. Wire it up the same way LoginPage/RegisterPage are
// wired in App.tsx: render it when the logged-in user is an admin and the
// user has navigated to it (see the two-line App.tsx patch at the bottom
// of this file's companion notes).
//
// Talks to api/admin.php using the same JWT the rest of the app uses.

import React, { useEffect, useState, useCallback } from 'react';
import { API_URL, APP_NAME } from '../config';
import './AdminDashboard.css';

interface AdminUser {
  id: number;
  email: string;
  name: string;
  avatar_url: string;
  is_admin: boolean;
  created_at: string;
  last_login_at: string | null;
  chat_count: number | null;
}

interface Stats {
  total_users: number;
  new_users_7d: number;
  new_users_30d: number;
  active_users_1d: number;
  active_users_7d: number;
  total_kb_documents: number | null;
  total_chats: number | null;
}

interface Props {
  token: string;
  onClose: () => void;
}

async function adminFetch(token: string, action: string, opts: RequestInit = {}) {
  const res = await fetch(`${API_URL}/admin.php?action=${action}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export default function AdminDashboard({ token, onClose }: Props) {
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 25;
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const loadStats = useCallback(async () => {
    try {
      const data = await adminFetch(token, 'stats');
      setStats(data);
    } catch (e: any) {
      setError(e.message);
    }
  }, [token]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ q: query, page: String(page), per_page: String(perPage) });
      const data = await adminFetch(token, `users&${params.toString()}`);
      setUsers(data.users);
      setTotal(data.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, query, page]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleDelete = async (id: number) => {
    try {
      await adminFetch(token, 'delete_user', { method: 'POST', body: JSON.stringify({ id }) });
      setConfirmDeleteId(null);
      loadUsers();
      loadStats();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleSave = async (patch: Partial<AdminUser> & { password?: string }) => {
    if (!editingUser) return;
    try {
      await adminFetch(token, 'update_user', {
        method: 'POST',
        body: JSON.stringify({ id: editingUser.id, ...patch }),
      });
      setEditingUser(null);
      loadUsers();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-logo">
          <svg className="admin-logo-svg" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="36" height="36" rx="10" fill="#a78bfa"/>
            <path d="M10 13h4l2 3-2 3h-4l-2-3 2-3z" fill="#0f0f17"/>
            <path d="M18 9l2 2-2 2-2-2 2-2z" fill="#0f0f17" opacity="0.6"/>
            <rect x="14" y="17" width="12" height="2" rx="1" fill="#0f0f17"/>
            <rect x="14" y="21" width="8" height="2" rx="1" fill="#0f0f17" opacity="0.7"/>
            <circle cx="26" cy="24" r="4" fill="#60a5fa"/>
            <path d="M24.5 24l1 1 2-2" stroke="#0f0f17" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div className="admin-logo-text-wrap">
            <span className="admin-logo-text">{APP_NAME}</span>
            <span className="admin-logo-sub">Admin</span>
          </div>
        </div>
        <button className="admin-btn admin-btn-ghost" onClick={onClose}>← Back to app</button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-stats-grid">
        <StatCard label="Total users" value={stats?.total_users} />
        <StatCard label="New (7d)" value={stats?.new_users_7d} />
        <StatCard label="New (30d)" value={stats?.new_users_30d} />
        <StatCard label="Active today" value={stats?.active_users_1d} />
        <StatCard label="Active (7d)" value={stats?.active_users_7d} />
        {stats?.total_kb_documents !== null && (
          <StatCard label="KB documents" value={stats?.total_kb_documents ?? undefined} />
        )}
        {stats?.total_chats !== null && (
          <StatCard label="Total chats" value={stats?.total_chats ?? undefined} />
        )}
      </section>

      <section className="admin-users-section">
        <div className="admin-toolbar">
          <input
            className="admin-search"
            placeholder="Search by name or email…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <span className="admin-count">{total} user{total === 1 ? '' : 's'}</span>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Name</th>
                <th>Email</th>
                <th>Admin</th>
                <th>Chats</th>
                <th>Registered</th>
                <th>Last login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="admin-empty">Loading…</td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={8} className="admin-empty">No users found</td></tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td>
                      {u.avatar_url
                        ? <img className="admin-avatar" src={u.avatar_url} alt="" />
                        : <div className="admin-avatar admin-avatar-placeholder">{u.name.charAt(0).toUpperCase()}</div>}
                    </td>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.is_admin ? <span className="admin-badge">Admin</span> : ''}</td>
                    <td>{u.chat_count ?? '—'}</td>
                    <td>{fmtDate(u.created_at)}</td>
                    <td>{fmtDate(u.last_login_at)}</td>
                    <td className="admin-row-actions">
                      <button className="admin-btn admin-btn-sm" onClick={() => setEditingUser(u)}>Edit</button>
                      <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => setConfirmDeleteId(u.id)}>Delete</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="admin-pagination">
          <button className="admin-btn admin-btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} of {totalPages}</span>
          <button className="admin-btn admin-btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </section>

      <footer className="admin-footer">
        <span>{APP_NAME} Admin · {new Date().getFullYear()}</span>
      </footer>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onCancel={() => setEditingUser(null)}
          onSave={handleSave}
        />
      )}

      {confirmDeleteId !== null && (
        <div className="admin-modal-backdrop" onClick={() => setConfirmDeleteId(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete this user?</h3>
            <p>This permanently removes their account. This can't be undone.</p>
            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-ghost" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={() => handleDelete(confirmDeleteId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value?: number }) {
  return (
    <div className="admin-stat-card">
      <div className="admin-stat-value">{value ?? '—'}</div>
      <div className="admin-stat-label">{label}</div>
    </div>
  );
}

function EditUserModal({
  user,
  onCancel,
  onSave,
}: {
  user: AdminUser;
  onCancel: () => void;
  onSave: (patch: Partial<AdminUser> & { password?: string }) => void;
}) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url || '');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(user.is_admin);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const patch: Partial<AdminUser> & { password?: string } = {
      name,
      email,
      avatar_url: avatarUrl,
      is_admin: isAdmin,
    };
    if (password) patch.password = password;
    onSave(patch);
  };

  return (
    <div className="admin-modal-backdrop" onClick={onCancel}>
      <form className="admin-modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>Edit user</h3>

        <label className="admin-field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        </label>

        <label className="admin-field">
          <span>Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>

        <label className="admin-field">
          <span>Avatar URL</span>
          <input value={avatarUrl} onChange={(e) => setAvatarUrl(e.target.value)} placeholder="https://…" />
        </label>

        <label className="admin-field">
          <span>New password (leave blank to keep current)</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} placeholder="••••••" />
        </label>

        <label className="admin-field admin-field-checkbox">
          <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
          <span>Admin access</span>
        </label>

        <div className="admin-modal-actions">
          <button type="button" className="admin-btn admin-btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="admin-btn admin-btn-primary">Save changes</button>
        </div>
      </form>
    </div>
  );
}